import { Select as SelectPrimitive } from "radix-ui";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@/lib/utils";

export interface SelectOption {
  value: string;
  label: string;
  subLabel?: string;
}

export interface SelectProps {
  options: SelectOption[];
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  "aria-label"?: string;
}

export function Select({
  options,
  value,
  onChange,
  placeholder = "Select an option...",
  className,
  "aria-label": ariaLabel,
}: SelectProps) {
  return (
    <div className={cn("w-full max-w-xl", className)}>
      <SelectPrimitive.Root value={value} onValueChange={onChange}>
        <SelectPrimitive.Trigger
          aria-label={ariaLabel || placeholder}
          className="relative flex w-full items-center justify-between cursor-pointer rounded-lg border-2 border-gray-500 bg-white py-3 pl-4 pr-3 text-left shadow-xs focus:border-themered focus:outline-none focus:ring-2 focus:ring-themered text-2xl md:text-base font-medium text-gray-900 transition duration-150 ease-in-out"
        >
          <span className="block truncate text-gray-700 text-fluid-body">
            <SelectPrimitive.Value placeholder={placeholder} />
          </span>
          <SelectPrimitive.Icon asChild>
            <ChevronDown className="h-6 w-6 text-gray-500 shrink-0" aria-hidden="true" />
          </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>

        <SelectPrimitive.Portal>
          <SelectPrimitive.Content
            position="popper"
            sideOffset={4}
            className="relative z-50 max-h-60 w-[var(--radix-select-trigger-width)] min-w-[8rem] overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black/5 text-base"
          >
            <SelectPrimitive.ScrollUpButton className="flex cursor-default items-center justify-center py-1">
              <ChevronUp className="h-4 w-4 text-gray-500" />
            </SelectPrimitive.ScrollUpButton>
            <SelectPrimitive.Viewport className="p-1">
              {options.map((option) => (
                <SelectPrimitive.Item
                  key={option.value}
                  value={option.value}
                  className="group relative flex w-full cursor-pointer select-none items-center rounded-md py-2.5 pl-8 pr-4 text-fluid-body text-gray-900 outline-none data-[highlighted]:bg-darkred data-[highlighted]:text-white transition-colors duration-100"
                >
                  <span className="absolute left-2 flex h-4 w-4 items-center justify-center">
                    <SelectPrimitive.ItemIndicator>
                      <Check className="h-4 w-4 text-themered group-data-[highlighted]:text-white" aria-hidden="true" />
                    </SelectPrimitive.ItemIndicator>
                  </span>
                  <SelectPrimitive.ItemText>
                    <span className="font-semibold text-gray-900 group-data-[highlighted]:text-white mr-2">
                      {option.label}
                    </span>
                    {option.subLabel && (
                      <span className="text-gray-600 group-data-[highlighted]:text-red-100">
                        {option.subLabel}
                      </span>
                    )}
                  </SelectPrimitive.ItemText>
                </SelectPrimitive.Item>
              ))}
            </SelectPrimitive.Viewport>
            <SelectPrimitive.ScrollDownButton className="flex cursor-default items-center justify-center py-1">
              <ChevronDown className="h-4 w-4 text-gray-500" />
            </SelectPrimitive.ScrollDownButton>
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>
    </div>
  );
}
